import { DateTime } from "luxon";

import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

interface IExternalEmployeeRawResponse {
    employeeId: string;
    firstName: string;
    lastName: string;
    organizationId: string;
    organizationName: string;
    siteId: string;
    siteName: string;
    clientTag: string;
    licenseNumber: string;
    prefixCode: string;
    prefixTypeCode: string;
    issuingLicenseOrganizationRuleId?: string;
}

class ExternalEmployeeResponse {
    public EmployeeId: string;
    public FirstName: string;
    public LastName: string;
    public OrganizationId: string;
    public OrganizationName: string;
    public SiteId: string;
    public SiteName: string;
    public ClientTag: string;
    public LicenseNumber: string;
    public PrefixLookupId: string;
    public PrefixCode: string;
    public PrefixTypeCode: string;
    public IssuingLicenseOrganizationRuleId?: string;
    public SiteAddress: ExternalEmployeeAddress;
    public SitePhones: ExternalEmployeePhone[];
    public LastLogin?: DateTime;

    public constructor(init?: Partial<ExternalEmployeeResponse>) {
        Object.assign(this, init);
    }

    public get displayName(): string {
        return ExternalEmployeeResponse.buildDisplayName(this.FirstName, this.LastName, this.PrefixLookupId, this.OrganizationName, this.SiteName);
    }

    public getNameWithPrefix(): string {
        return ExternalEmployeeResponse.buildEmployeeName(this.FirstName, this.LastName, this.PrefixLookupId);
    }

    public static fromResponse(emp: Partial<ExternalEmployeeResponse>): ExternalEmployeeResponse {
        if (!emp)
            return null;

        return (Object as any).assign(new ExternalEmployeeResponse(), emp);
    }

    public static fromEmployeeRegResponse(employee: any): ExternalEmployeeResponse {
        if (!employee)
            return null;

        const model = new ExternalEmployeeResponse({
            EmployeeId: employee.employeeId,
            FirstName: employee.firstName,
            LastName: employee.lastName,
            OrganizationId: employee.organizationId,
            OrganizationName: employee.organizationName,
            SiteId: employee.siteId,
            SiteName: employee.siteName,
            ClientTag: employee.clientTag,
            LicenseNumber: employee.licenseNumber,
            IssuingLicenseOrganizationRuleId: employee.issuingLicenseOrganizationRuleId,
            PrefixCode: employee.prefixCode,
            PrefixTypeCode: employee.prefixTypeCode,
            LastLogin: employee.lastLogin ? LuxonDateTimeParser.parse(employee.lastLogin) : null
        });

        model.PrefixLookupId = ExternalEmployeeResponse.getLookupIdIfAvailable(model.PrefixCode, model.PrefixTypeCode);
        model.SitePhones = employee.sitePhones?.map(phone => ({
            Number: phone.number,
            PhoneTypeCode: phone.phoneTypeCode,
            PhoneCode: phone.phoneCode,
            PhoneTypeLookupId: ExternalEmployeeResponse.getLookupIdIfAvailable(phone.phoneCode, phone.phoneTypeCode),
            CountryCode: phone.countryCode,
            CountryTypeCode: phone.countryTypeCode,
            Country: ExternalEmployeeResponse.getLookupIdIfAvailable(phone.CountryCode, phone.CountryTypeCode),
            StateTypeCode: phone.stateTypeCode,
            StateCode: phone.stateCode,
            State: ExternalEmployeeResponse.getLookupIdIfAvailable(phone.StateCode, phone.StateTypeCode),
            Extension: phone.extension,
            IsPrefered: phone.isPrefered
        }));
        if (employee.siteAddress) {
            model.SiteAddress = new ExternalEmployeeAddress({
                StreetAddress: employee.siteAddress.streetAddress,
                City: employee.siteAddress.city,
                ZipCode: employee.siteAddress.zipCode,
                CountryCode: employee.siteAddress.countryCode,
                CountryTypeCode: employee.siteAddress.countryTypeCode,
                CountryLookupId: ExternalEmployeeResponse.getLookupIdIfAvailable(employee.siteAddress.countryCode, employee.siteAddress.countryTypeCode),
                StateCode: employee.siteAddress.stateCode,
                StateTypeCode: employee.siteAddress.stateTypeCode,
                StateLookupId: ExternalEmployeeResponse.getLookupIdIfAvailable(employee.siteAddress.stateCode, employee.siteAddress.stateTypeCode),
                IsPrefered: employee.siteAddress.isPrefered
            });
        }

        return model;
    }

    private static getLookupIdIfAvailable(code: string, typeCode: string): string | null {
        if (IsNullOrWhiteSpace(code) || IsNullOrWhiteSpace(typeCode))
            return null;

        return cacheStore.FindLookupByLookupCode(code, typeCode)?.LookupID;
    }

    public getFormattedAddress(): string {
        if (!this.SiteAddress)
            return null;

        return [this.SiteAddress.StreetAddress, this.SiteAddress.City, this.SiteAddress.ZipCode].filter(v => !!v).join(", ");
    }

    public static buildDisplayName(firstName: string, lastName: string, prefixLookupId?: string, orgName?: string, siteName?: string): string {
        let displayName = ExternalEmployeeResponse.buildEmployeeName(firstName, lastName, prefixLookupId);

        if (orgName || siteName)
            displayName += ` (${ExternalEmployeeResponse.buildDisplayClinicName(orgName, siteName)})`;

        return displayName;
    }

    public static buildEmployeeName(firstName: string, lastName: string, prefixLookupId?: string): string {
        let displayName = `${lastName}, ${firstName}`;

        let prefix = cacheStore.GetLocalizedValueByLookupId(prefixLookupId);
        if (prefix)
            displayName = `${prefix} ${displayName}`;

        return displayName;
    }

    public static buildDisplayClinicName(orgName: string, siteName: string): string {
        return `${orgName ? orgName + " - " : ""}${siteName || ""}`;
    }
}

class ExternalEmployeeAddress {
    public StreetAddress: string;
    public City: string;
    public ZipCode: string;
    public CountryLookupId: string;
    public CountryCode: string;
    public CountryTypeCode: string;
    public StateLookupId: string;
    public StateCode: string;
    public StateTypeCode: string;
    public IsPrefered: boolean;

    constructor(field?: Partial<ExternalEmployeeAddress>) {
        Object.assign(this, field);
    }
}

class ExternalEmployeePhone {
    public Number: string;
    public PhoneTypeCode: string;
    public PhoneCode: string;
    public PhoneTypeLookupId: string;
    public CountryCode: string;
    public CountryTypeCode: string;
    public Country: string;
    public StateTypeCode: string;
    public StateCode: string;
    public State: string;
    public Extension: string;
    public IsPrefered: boolean;
}

export { ExternalEmployeeAddress, ExternalEmployeePhone, ExternalEmployeeResponse, IExternalEmployeeRawResponse };
